import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button as CrcButton,
  Checkbox as CrcCheckbox,
} from '@crc-evans/ui-components';
import {
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Spin,
  Table,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Plus, FileText, AlertTriangle, Share2 } from 'lucide-react';
import moment from 'moment';
import DSRSignaturePad from '../DSR/DSRSignaturePad';
import {
  authoriseCertificate,
  createCertificate,
  fetchCertificate,
  fetchCertificatePdf,
  fetchEquipmentProfile,
  fetchEquipmentProfiles,
  submitCertificateForReview,
  updateCertificate,
} from '../../services/arcWelding';
import {
  getProceduralConfirmationFields,
  useArcWeldingFormOptions,
} from '../../hooks/useArcWeldingFormOptions';
import {
  AW_CERTIFICATE_STATUS,
  AW_CERTIFICATE_SECTIONS,
  AW_FORM_LABELS,
  AW_PARAMETERS,
  buildAuthoriseRequestBody,
  buildProceduralConfirmationsPayload,
  buildSaveAuthorisationsPayload,
  buildTestPointsPayload,
  findAuthorisationByRole,
  findParameterBlock,
  formatBlockingReasonShort,
  formatPermittedToleranceDisplay,
  formatSaveValidationNotification,
  getCertificateWorkflowNotice,
  hasSavedAuthorisationSignature,
  isCertificateEditable,
  resolveCertificateDetail,
  mapAuthorisationFromApi,
  mergeAuthorisationFromApi,
  mapReferenceInstrumentFromApi,
  mapReferenceInstrumentToApi,
  mapTestPointFromApi,
  methodForClassification,
  normalizePagedResponse,
  normalizeProceduralConfirmationsFromApi,
  openPdfUrl,
  PARAMETER_CLASSIFICATION_KEYS,
  resolvePdfUrl,
  resolveSiteFields,
} from '../../utils/arcWeldingVerificationUi';
import { AwFormSelect, mergeSelectOptions } from './ArcWeldingListShell';
import ArcWeldingTestPointBlock from './ArcWeldingTestPointBlock';
import ArcWeldingShareModal from './ArcWeldingShareModal';
import './ArcWelding.css';

const { TextArea } = Input;

const CRC_CHECKBOX_PROPS = {
  borderColor: '#00C9A7',
  tickColor: '#00C9A7',
};

const readCheckboxChecked = (checkedOrEvent) =>
  typeof checkedOrEvent === 'boolean' ? checkedOrEvent : (checkedOrEvent?.target?.checked ?? false);

const datePickerProps = {
  format: 'DD-MMM-YYYY',
  placeholder: 'dd-mm-yyyy',
  suffixIcon: <CalendarOutlined />,
  dropdownClassName: 'heat-profile-datepicker-dropdown',
  getPopupContainer: () => document.body,
};

const emptyRefInstrument = () => ({
  key: Date.now(),
  instrumentName: '',
  serialNumber: '',
  calibrationCertNo: '',
  calibrationExpiryDate: null,
});

const AwSignatureField = ({ label, value, disabled, onChange, helperText }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleSave = (dataUrl) => {
    if (!dataUrl) {
      onChange?.({
        name: value?.name || '',
        signatureBase64: null,
        signatureDataUrl: null,
        signedAt: null,
        signatureS3Key: null,
      });
      return;
    }
    const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
    onChange?.({
      ...value,
      signatureBase64: base64,
      signatureDataUrl: dataUrl,
      signedAt: new Date().toISOString(),
    });
  };

  const openDrawer = () => {
    if (disabled) return;
    setDrawerOpen(true);
  };

  return (
    <div className="aw-signature-field">
      <div className="aw-signature-block-label">{label}</div>
      <Input
        className="aw-signature-name-input"
        placeholder="Name"
        value={value?.name}
        disabled={disabled}
        onChange={(e) => onChange?.({ ...value, name: e.target.value })}
      />
      {value?.signatureDataUrl ? (
        <button
          type="button"
          className={`aw-signature-preview-wrap${disabled ? ' aw-signature-preview-wrap--disabled' : ''}`}
          onClick={openDrawer}
          disabled={disabled}
        >
          <img src={value.signatureDataUrl} alt="saved signature" className="aw-signature-preview" />
          {!disabled && (
            <span className="aw-signature-edit-hint">
              <EditOutlined /> Edit Signature
            </span>
          )}
        </button>
      ) : hasSavedAuthorisationSignature(value) ? (
        <button
          type="button"
          className={`aw-signature-preview-wrap aw-signature-preview-wrap--on-file${disabled ? ' aw-signature-preview-wrap--disabled' : ''}`}
          onClick={openDrawer}
          disabled={disabled}
        >
          <span className="aw-signature-on-file-label">Signature on file</span>
          {!disabled && (
            <span className="aw-signature-edit-hint">
              <EditOutlined /> Edit Signature
            </span>
          )}
        </button>
      ) : (
        <button
          type="button"
          className="aw-signature-add-btn"
          onClick={openDrawer}
          disabled={disabled}
        >
          <EditOutlined /> Add Signature
        </button>
      )}
      {helperText && <p className="aw-signature-helper">{helperText}</p>}
      {!disabled && (
        <Drawer
          title={`${label} Signature`}
          placement="right"
          width={520}
          onClose={() => setDrawerOpen(false)}
          visible={drawerOpen}
          destroyOnClose
        >
          <DSRSignaturePad
            visible={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onSave={handleSave}
            title={label}
            initialSignature={value?.signatureDataUrl}
          />
        </Drawer>
      )}
    </div>
  );
};

const AwWorkflowNotice = ({ title, intro, blockingReasons = [] }) => {
  const [dismissed, setDismissed] = useState(false);
  const uniqueReasons = useMemo(
    () => [...new Set((blockingReasons ?? []).map((r) => String(r).trim()).filter(Boolean))],
    [blockingReasons]
  );
  const reasonsKey = uniqueReasons.join('\n');

  useEffect(() => {
    setDismissed(false);
  }, [reasonsKey]);

  if (!uniqueReasons.length || dismissed) return null;

  return (
    <div className="aw-workflow-notice aw-workflow-notice--blocking" role="alert">
      <div className="aw-workflow-notice-header">
        <AlertTriangle size={18} aria-hidden className="aw-workflow-notice-icon" />
        <div className="aw-workflow-notice-body">
          <strong className="aw-workflow-notice-title">{title}</strong>
          {intro && <p className="aw-workflow-notice-intro">{intro}</p>}
          <ul className="aw-blocking-reasons aw-blocking-reasons--compact">
            {uniqueReasons.map((r) => (
              <li key={r}>{formatBlockingReasonShort(r)}</li>
            ))}
          </ul>
        </div>
        <button
          type="button"
          className="aw-workflow-notice-close"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
        >
          <CloseOutlined />
        </button>
      </div>
    </div>
  );
};

const requiredRule = (label) => [{ required: true, message: `${label} is required` }];

/** Expiry = test/issue date + interval months (BE uses same rule; field stays read-only). */
const computeExpiryDate = (testIssueDate, intervalMonths = 12) => {
  if (!testIssueDate) return null;
  const months = Number(intervalMonths);
  return moment(testIssueDate).add(Number.isFinite(months) && months > 0 ? months : 12, 'months');
};

const syncExpiryDateField = (formInstance, testIssueDate, intervalMonths) => {
  formInstance.setFieldsValue({
    expiryDate: computeExpiryDate(testIssueDate, intervalMonths),
  });
};

const notifySaveError = (err, formInstance) => {
  const { message, description, isValidation } = formatSaveValidationNotification(err);
  if (isValidation && err?.errorFields?.[0]?.name) {
    formInstance?.scrollToField?.(err.errorFields[0].name, { behavior: 'smooth', block: 'center' });
  }
  notification.error({ message, description, duration: 6 });
};

const ArcWeldingCertificateForm = ({
  certificateId,
  formMode = 'edit',
  projects = [],
  onBack,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const { options: formOptions } = useArcWeldingFormOptions();
  const proceduralFields = useMemo(
    () => getProceduralConfirmationFields(formOptions),
    [formOptions]
  );
  const siteOptions = useMemo(
    () => (formOptions.sites ?? []).map((s) => ({
      siteCode: s.siteCode ?? s.code,
      siteName: s.siteName ?? s.name,
      siteId: s.siteId ?? s.id,
    })),
    [formOptions.sites]
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profileDetail, setProfileDetail] = useState(null);
  const [manualOverride, setManualOverride] = useState(false);
  const [parameterState, setParameterState] = useState({});
  const [refInstruments, setRefInstruments] = useState([emptyRefInstrument()]);
  const [procedural, setProcedural] = useState({});
  const [technicianAuth, setTechnicianAuth] = useState({});
  const [supervisorAuth, setSupervisorAuth] = useState({});

  const projectSelectOptions = useMemo(() => {
    const opts = projects.map((p) => ({
      value: String(p.id),
      label: p.projectName || p.projectId || `Project ${p.id}`,
    }));
    return mergeSelectOptions(
      opts,
      certificate?.projectId,
      certificate?.projectName
    );
  }, [projects, certificate?.projectId, certificate?.projectName]);

  const siteSelectOptions = useMemo(() => {
    const resolved = resolveSiteFields(certificate ?? {}, siteOptions);
    const opts = siteOptions.map((s) => ({
      value: String(s.siteCode),
      label: s.siteName ? `${s.siteName} (${s.siteCode})` : String(s.siteCode),
    }));
    return mergeSelectOptions(
      opts,
      resolved.siteCode,
      resolved.siteName
        ? `${resolved.siteName}${resolved.siteCode ? ` (${resolved.siteCode})` : ''}`
        : resolved.siteCode
    );
  }, [siteOptions, certificate?.siteCode, certificate?.siteName]);

  const equipmentProfileSelectOptions = useMemo(() => {
    const opts = profiles.map((p) => ({
      value: String(p.id),
      label: [p.profileName, p.manufacturer, p.model].filter(Boolean).join(' — '),
    }));
    const currentId = certificate?.equipmentProfileId ?? profileDetail?.id;
    const currentLabel = profileDetail
      ? [profileDetail.profileName, profileDetail.manufacturer, profileDetail.model].filter(Boolean).join(' — ')
      : certificate?.profileName;
    return mergeSelectOptions(opts, currentId, currentLabel);
  }, [
    profiles,
    certificate?.equipmentProfileId,
    certificate?.profileName,
    profileDetail,
  ]);

  const status = certificate?.status ?? AW_CERTIFICATE_STATUS.DRAFT;
  const readOnly = formMode === 'view' || !isCertificateEditable(status);
  const isIssued = status === AW_CERTIFICATE_STATUS.ISSUED || status === AW_CERTIFICATE_STATUS.SUPERSEDED;

  const loadProfiles = useCallback(async () => {
    try {
      const payload = await fetchEquipmentProfiles({ isActive: true, pageSize: 500 });
      const paged = normalizePagedResponse(payload);
      setProfiles(paged.items);
    } catch {
      setProfiles([]);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const applyCertificate = useCallback(
    (data, { mergeAuthorisations = false, prevTechnicianAuth, prevSupervisorAuth } = {}) => {
      const detail = data ?? {};
      setCertificate(detail);
      const { siteCode, siteName } = resolveSiteFields(detail, siteOptions);
      form.setFieldsValue({
        certificateNo: detail.certificateNo,
        projectId: detail.projectId != null && detail.projectId !== '' ? String(detail.projectId) : undefined,
        siteCode,
        siteName,
        equipmentProfileId:
          detail.equipmentProfileId != null && detail.equipmentProfileId !== ''
            ? String(detail.equipmentProfileId)
            : undefined,
        serialNumber: detail.serialNumber,
        assetPlantNo: detail.assetPlantNo,
        testIssueDate: detail.testIssueDate ? moment(detail.testIssueDate) : null,
        intervalMonths: detail.intervalMonths ?? 12,
        expiryDate: detail.expiryDate ? moment(detail.expiryDate) : null,
        process: detail.process,
        outputMode: detail.outputMode,
        grade: detail.grade,
        verificationRange: detail.verificationRange,
        rangeRecorded: detail.rangeRecorded,
        supplyVoltageV: detail.supplyVoltageV ?? detail.supplyVoltage,
        supplyFrequency: detail.supplyFrequency ?? detail.frequency,
        ambientTempC: detail.ambientTempC,
        wfsUnit: detail.wfsUnit ?? 'm/min',
        remarks: detail.remarks,
      });
      syncExpiryDateField(
        form,
        detail.testIssueDate ? moment(detail.testIssueDate) : null,
        detail.intervalMonths ?? 12
      );
      if (detail.expiryDate) {
        form.setFieldsValue({ expiryDate: moment(detail.expiryDate) });
      }
      setManualOverride(Boolean(detail.manualOverrideFlag ?? detail.manualProfileOverride));
      const paramMap = {};
      AW_PARAMETERS.forEach((param) => {
        const block = findParameterBlock(detail.parameterBlocks, param);
        const classificationKey = PARAMETER_CLASSIFICATION_KEYS[param];
        const rawPoints = detail.testPoints?.filter((p) => p.parameter === param) ?? block.points ?? [];
        const classification = detail[classificationKey] ?? block.valueClassification;
        paramMap[param] = {
          valueClassification: classification,
          method: block.method ?? methodForClassification(classification),
          permittedTolerance: block.permittedTolerance,
          permittedToleranceLow: block.permittedToleranceLow,
          permittedToleranceHigh: block.permittedToleranceHigh,
          referenceMax: block.referenceMax,
          sectionResult: detail.sectionResults?.[param] ?? block.sectionResult,
          points: rawPoints.map(mapTestPointFromApi),
        };
      });
      setParameterState(paramMap);
      const refs = detail.referenceInstruments?.length
        ? detail.referenceInstruments.map((r, i) => mapReferenceInstrumentFromApi(r, i))
        : [emptyRefInstrument()];
      setRefInstruments(refs);
      setProcedural(
        normalizeProceduralConfirmationsFromApi(detail.proceduralConfirmations ?? detail.procedural)
      );
      const auths = detail.authorisations ?? detail.authorisation ?? [];
      const tech = findAuthorisationByRole(auths, 'Technician') ?? {};
      const sup = findAuthorisationByRole(auths, 'Supervisor') ?? {};
      if (mergeAuthorisations) {
        setTechnicianAuth(
          mergeAuthorisationFromApi(tech, prevTechnicianAuth, detail.technicianName)
        );
        setSupervisorAuth(
          mergeAuthorisationFromApi(
            sup,
            prevSupervisorAuth,
            detail.supervisorName ?? prevSupervisorAuth?.name
          )
        );
      } else {
        setTechnicianAuth(mapAuthorisationFromApi(tech, detail.technicianName));
        setSupervisorAuth(mapAuthorisationFromApi(sup, detail.supervisorName));
      }
      if (detail.equipmentProfile) setProfileDetail(detail.equipmentProfile);
    },
    [form, siteOptions]
  );

  useEffect(() => {
    if (!certificate) return;
    const { siteCode, siteName } = resolveSiteFields(certificate, siteOptions);
    if (!siteCode && !siteName) return;
    form.setFieldsValue({
      siteCode,
      siteName: siteName ?? form.getFieldValue('siteName'),
    });
  }, [certificate, siteOptions, form]);

  const loadCertificate = useCallback(async () => {
    if (!certificateId) return;
    setLoading(true);
    try {
      const data = await fetchCertificate(certificateId);
      applyCertificate(data);
    } catch (err) {
      notification.error({ message: 'Failed to load certificate', description: String(err?.message || err) });
    } finally {
      setLoading(false);
    }
  }, [certificateId, applyCertificate]);

  useEffect(() => {
    if (certificateId) loadCertificate();
    else {
      form.resetFields();
      form.setFieldsValue({ intervalMonths: 12, wfsUnit: 'm/min' });
      syncExpiryDateField(form, null, 12);
      setCertificate(null);
      setParameterState({});
      setRefInstruments([emptyRefInstrument()]);
      setProcedural({});
      setTechnicianAuth({});
      setSupervisorAuth({});
      setProfileDetail(null);
    }
  }, [certificateId, loadCertificate, form]);

  const handleProfileSelect = async (profileId) => {
    if (!profileId) {
      setProfileDetail(null);
      return;
    }
    try {
      const profile = await fetchEquipmentProfile(profileId);
      setProfileDetail(profile);
      if (!manualOverride) {
        form.setFieldsValue({
          grade: profile.defaultGrade,
          process: profile.defaultProcess,
          outputMode: profile.defaultOutputMode,
          verificationRange: profile.defaultVerificationRange,
          supplyFrequency: profile.defaultFrequency,
        });
        const classification = profile.defaultValueClassification;
        if (classification) {
          const next = { ...parameterState };
          AW_PARAMETERS.forEach((p) => {
            next[p] = { ...next[p], valueClassification: classification };
          });
          setParameterState(next);
        }
      }
    } catch (err) {
      notification.error({ message: 'Failed to load profile', description: String(err?.message || err) });
    }
  };

  const buildPayload = (values) => {
    const toApiId = (v) => {
      if (v == null || v === '') return null;
      const n = Number(v);
      return Number.isNaN(n) ? v : n;
    };

    const siteCode = values.siteCode;
    let siteName = values.siteName;
    if (siteCode) {
      const site = siteOptions.find((s) => String(s.siteCode) === String(siteCode));
      siteName = siteName || site?.siteName || '';
    }

    const referenceInstrumentsPayload = refInstruments
      .map((row, index) => mapReferenceInstrumentToApi(row, index))
      .filter(
        (row) =>
          row.instrumentName
          || row.serialNumber
          || row.calibrationCertNo
          || row.calibrationExpiryDate
      );

    return {
    projectId: toApiId(values.projectId),
    siteCode: siteCode ? String(siteCode) : null,
    siteName: siteName || null,
    equipmentProfileId: toApiId(values.equipmentProfileId),
    serialNumber: values.serialNumber,
    assetPlantNo: values.assetPlantNo,
    testIssueDate: values.testIssueDate?.format?.('YYYY-MM-DD') ?? values.testIssueDate,
    intervalMonths: values.intervalMonths,
    process: values.process,
    outputMode: values.outputMode,
    grade: values.grade,
    verificationRange: values.verificationRange,
    rangeRecorded: values.rangeRecorded,
    supplyVoltageV: values.supplyVoltageV,
    supplyFrequency: values.supplyFrequency,
    ambientTempC: values.ambientTempC,
    wfsUnit: values.wfsUnit,
    remarks: values.remarks,
    manualOverrideFlag: manualOverride,
    currentValueClassification: parameterState.Current?.valueClassification,
    voltageValueClassification: parameterState.Voltage?.valueClassification,
    wfsValueClassification: parameterState['Wire feed speed']?.valueClassification,
    referenceInstruments: referenceInstrumentsPayload,
    proceduralConfirmations: buildProceduralConfirmationsPayload(procedural),
    testPoints: buildTestPointsPayload(parameterState),
    authorisations: buildSaveAuthorisationsPayload(technicianAuth, supervisorAuth, status),
    sourceChannel: 'Web',
    };
  };

  const persistCertificate = async () => {
    const values = await form.validateFields();
    const payload = buildPayload(values);
    const prevTechnicianAuth = technicianAuth;
    const prevSupervisorAuth = supervisorAuth;
    let id = certificate?.id;

    if (!id) {
      const created = await createCertificate(payload);
      id = created?.id ?? created?.certificateId;
      if (!id) throw new Error('Failed to create certificate');
    }

    const putResponse = await updateCertificate(id, payload);
    let saved = await resolveCertificateDetail(putResponse, id, fetchCertificate);

    const sentSupervisorName = payload.authorisations?.find((a) => a.role === 'Supervisor')?.name;
    if (sentSupervisorName) {
      const responseSup = findAuthorisationByRole(saved?.authorisations, 'Supervisor');
      const responseName = String(responseSup?.name ?? saved?.supervisorName ?? '').trim();
      if (!responseName) {
        saved = await fetchCertificate(id);
      }
    }

    if (saved) {
      applyCertificate(saved, {
        mergeAuthorisations: true,
        prevTechnicianAuth,
        prevSupervisorAuth,
      });
    }
    return { id, saved };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { saved } = await persistCertificate();
      notification.success({ message: 'Certificate saved' });
      onSaved?.(saved);
    } catch (err) {
      notifySaveError(err, form);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const { id } = await persistCertificate();
      if (!id) return;
      const updated = await submitCertificateForReview(id);
      const detail = await resolveCertificateDetail(updated, id, fetchCertificate);
      applyCertificate(detail, {
        mergeAuthorisations: true,
        prevTechnicianAuth: technicianAuth,
        prevSupervisorAuth: supervisorAuth,
      });
      notification.success({ message: 'Submitted for review' });
    } catch (err) {
      notifySaveError(err, form);
    } finally {
      setSaving(false);
    }
  };

  const handleAuthorise = async () => {
    const siteCode = form.getFieldValue('siteCode');
    if (!siteCode) {
      notification.warning({
        message: 'Please fill all required fields',
        description: `${AW_FORM_LABELS.crcSite} is required`,
      });
      form.scrollToField('siteCode', { behavior: 'smooth', block: 'center' });
      return;
    }
    const authoriseBody = buildAuthoriseRequestBody(siteCode, supervisorAuth);
    if (!authoriseBody.authorisations.length) {
      notification.warning({
        message: 'Supervisor signature required',
        description: 'Enter supervisor name and signature before authorising.',
      });
      return;
    }
    setSaving(true);
    try {
      const { id } = await persistCertificate();
      if (!id) return;
      const updated = await authoriseCertificate(id, authoriseBody);
      const detail = await resolveCertificateDetail(updated, id, fetchCertificate);
      applyCertificate(detail, {
        mergeAuthorisations: true,
        prevTechnicianAuth: technicianAuth,
        prevSupervisorAuth: supervisorAuth,
      });
      const pdfUrl = resolvePdfUrl(updated) ?? detail?.pdfFileUrl;
      if (pdfUrl) openPdfUrl(pdfUrl);
      notification.success({ message: 'Certificate issued' });
    } catch (err) {
      notifySaveError(err, form);
    } finally {
      setSaving(false);
    }
  };

  const handleViewPdf = async () => {
    if (!certificate?.id) return;
    const directUrl = certificate?.pdfFileUrl;
    if (directUrl) {
      openPdfUrl(directUrl);
      return;
    }
    try {
      const result = await fetchCertificatePdf(certificate.id);
      const url = resolvePdfUrl(result);
      if (url) openPdfUrl(url);
    } catch (err) {
      notification.error({ message: 'PDF not available', description: String(err?.message || err) });
    }
  };

  const workflowNotice = useMemo(() => {
    const base = getCertificateWorkflowNotice(certificate);
    // Authorise requirements are validated on button click, not via a blocking banner.
    if (base.context === 'authorise') {
      return { ...base, reasons: [] };
    }
    return base;
  }, [certificate]);

  const overallResult = certificate?.overallResult ?? 'Incomplete';

  const refColumns = [
    {
      title: 'Instrument Name',
      dataIndex: 'instrumentName',
      render: (v, row) =>
        readOnly ? v : (
          <Input
            value={v}
            onChange={(e) =>
              setRefInstruments((prev) =>
                prev.map((r) => (r.key === row.key ? { ...r, instrumentName: e.target.value } : r))
              )
            }
          />
        ),
    },
    {
      title: 'Serial',
      dataIndex: 'serialNumber',
      render: (v, row) =>
        readOnly ? v : (
          <Input
            value={v}
            onChange={(e) =>
              setRefInstruments((prev) =>
                prev.map((r) => (r.key === row.key ? { ...r, serialNumber: e.target.value } : r))
              )
            }
          />
        ),
    },
    {
      title: 'Certificate No.',
      dataIndex: 'calibrationCertNo',
      render: (v, row) =>
        readOnly ? v : (
          <Input
            value={v}
            onChange={(e) =>
              setRefInstruments((prev) =>
                prev.map((r) =>
                  r.key === row.key ? { ...r, calibrationCertNo: e.target.value } : r
                )
              )
            }
          />
        ),
    },
    {
      title: 'Cal. Expiry',
      dataIndex: 'calibrationExpiryDate',
      render: (v, row) =>
        readOnly ? (
          v ? moment(v).format('YYYY-MM-DD') : '—'
        ) : (
          <DatePicker
            {...datePickerProps}
            className="aw-form-datepicker"
            value={v ? moment(v) : null}
            onChange={(d) =>
              setRefInstruments((prev) =>
                prev.map((r) =>
                  r.key === row.key
                    ? { ...r, calibrationExpiryDate: d ? d.format('YYYY-MM-DD') : null }
                    : r
                )
              )
            }
          />
        ),
    },
    ...(!readOnly
      ? [{
          title: '',
          key: 'del',
          width: 56,
          align: 'center',
          render: (_, row) => (
            <div className="heat-profile-actions-cell aw-ref-instruments-actions">
              <Tooltip title={refInstruments.length <= 1 ? 'At least one row required' : 'Remove'}>
                <DeleteOutlined
                  className={`txtIcon aw-ref-instruments-delete${refInstruments.length <= 1 ? ' aw-ref-instruments-delete--disabled' : ''}`}
                  onClick={() => {
                    if (refInstruments.length <= 1) return;
                    setRefInstruments((prev) => prev.filter((r) => r.key !== row.key));
                  }}
                />
              </Tooltip>
            </div>
          ),
        }]
      : []),
  ];

  const profileFields = useMemo(() => {
    if (!profileDetail) return null;
    return [
      ['Type', profileDetail.equipmentType],
      ['Manufacturer', profileDetail.manufacturer],
      ['Model', profileDetail.model],
      ['Current (A)', `${profileDetail.currentMinA ?? '—'} – ${profileDetail.currentMaxA ?? '—'}`],
      ['Voltage (V)', `${profileDetail.voltageMinV ?? '—'} – ${profileDetail.voltageMaxV ?? '—'}`],
      ['Wire Feed Speed (m/min)', `${profileDetail.wfsMinMPerMin ?? '—'} – ${profileDetail.wfsMaxMPerMin ?? '—'}`],
      ['U0 (V)', profileDetail.ratedNoLoadU0V],
    ];
  }, [profileDetail]);

  const renderHeaderActions = () => {
    if (readOnly) {
      return (
        <>
          {status === AW_CERTIFICATE_STATUS.ISSUED && (
            <CrcButton
              label="Share"
              variant="secondary"
              size="small"
              icon={Share2}
              onClick={() => setShareModalOpen(true)}
            />
          )}
          {isIssued && (
            <CrcButton
              label="View PDF"
              variant="secondary"
              size="small"
              icon={FileText}
              onClick={handleViewPdf}
            />
          )}
          <CrcButton label="Close" variant="tertiary" size="small" onClick={onBack} />
        </>
      );
    }

    return (
      <>
        <CrcButton label="Cancel" variant="tertiary" size="small" onClick={onBack} />
        <CrcButton
          label={saving ? 'Saving…' : 'Save'}
          variant="secondary"
          size="small"
          onClick={handleSave}
          loading={saving}
          disabled={saving}
        />
        {status === AW_CERTIFICATE_STATUS.DRAFT && (
          <CrcButton
            label={saving ? 'Submitting…' : 'Submit for Review'}
            variant="primary"
            size="small"
            onClick={handleSubmit}
            loading={saving}
            disabled={saving || certificate?.canSubmitForReview === false}
          />
        )}
        {status === AW_CERTIFICATE_STATUS.AWAITING_REVIEW && (
          <CrcButton
            label={saving ? 'Authorising…' : 'Authorise & Issue'}
            variant="primary"
            size="small"
            onClick={handleAuthorise}
            loading={saving}
            disabled={saving}
          />
        )}
      </>
    );
  };

  return (
    <Spin spinning={loading}>
      <div className="aw-certificate-form heat-profile-content-wrapper">
        <div className="aw-form-page-header">
          <div className="aw-form-page-header-top">
            <div className="aw-form-page-title-group">
              <h1 className="aw-form-page-title">Arc Welding Verification Certificate</h1>
              {certificate?.certificateNo && (
                <p className="aw-form-page-subtitle">{certificate.certificateNo}</p>
              )}
            </div>
            <div className="aw-form-status-chips">
              <span className={`aw-dpr-chip aw-dpr-chip--result aw-dpr-chip--result-${overallResult.toLowerCase()}`}>
                <span className="aw-dpr-chip-label">Overall Result</span>
                <span className="aw-dpr-chip-value">{overallResult}</span>
              </span>
              <span className={`aw-dpr-chip aw-dpr-chip--status aw-dpr-chip--status-${status.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="aw-dpr-chip-label">Status</span>
                <span className="aw-dpr-chip-value">{status}</span>
              </span>
            </div>
          </div>
          <div className="aw-form-page-header-bottom">
            <button
              type="button"
              className="heat-profile-page-back-link aw-form-back-btn"
              onClick={onBack}
            >
              <span className="heat-profile-back-link">
                <ArrowLeftOutlined /> Back to List
              </span>
            </button>
            <div className="heat-profile-header-actions calibration-modern-actions aw-form-header-actions">
              {renderHeaderActions()}
            </div>
          </div>
        </div>

        <AwWorkflowNotice
          title={workflowNotice.title}
          intro={workflowNotice.intro}
          blockingReasons={workflowNotice.reasons}
        />

        <Form
          form={form}
          layout="vertical"
          disabled={readOnly}
          onValuesChange={(changed, all) => {
            if ('testIssueDate' in changed || 'intervalMonths' in changed) {
              syncExpiryDateField(form, all.testIssueDate, all.intervalMonths);
            }
          }}
        >
          <Row gutter={16} className="aw-details-profile-row" align="stretch">
            <Col xs={24} lg={14} xl={15}>
              <div className="aw-section aw-section--form aw-section--cert-details">
                <div className="aw-section-title">{AW_CERTIFICATE_SECTIONS.HEADER}</div>
                <div className="aw-cert-details-grid">
                  {certificate?.certificateNo && (
                    <Form.Item name="certificateNo" label={AW_FORM_LABELS.certificateNo} className="aw-cert-field">
                      <Input disabled />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="projectId"
                    label={AW_FORM_LABELS.project}
                    rules={requiredRule(AW_FORM_LABELS.project)}
                    className="aw-cert-field"
                  >
                    <AwFormSelect
                      options={projectSelectOptions}
                      placeholder="Select Project"
                      showSearch
                      optionFilterProp="children"
                    />
                  </Form.Item>
                  <Form.Item
                    name="siteCode"
                    label={AW_FORM_LABELS.crcSite}
                    rules={requiredRule(AW_FORM_LABELS.crcSite)}
                    className="aw-cert-field"
                  >
                    <AwFormSelect
                      options={siteSelectOptions}
                      placeholder="Select CRC Site"
                      showSearch
                      onAfterChange={(code) => {
                        const site = siteOptions.find((s) => String(s.siteCode) === String(code));
                        form.setFieldsValue({
                          siteName: site?.siteName ?? '',
                        });
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="serialNumber"
                    label={AW_FORM_LABELS.serialNumber}
                    rules={requiredRule(AW_FORM_LABELS.serialNumber)}
                    className="aw-cert-field"
                  >
                    <Input placeholder="Enter Serial Number" />
                  </Form.Item>
                  <Form.Item name="assetPlantNo" label={AW_FORM_LABELS.assetPlantNo} className="aw-cert-field">
                    <Input placeholder="Enter Asset / Plant No." />
                  </Form.Item>
                  <Form.Item
                    name="equipmentProfileId"
                    label={AW_FORM_LABELS.equipmentProfile}
                    className="aw-cert-field"
                  >
                    <AwFormSelect
                      options={equipmentProfileSelectOptions}
                      placeholder="Search profile"
                      allowClear
                      showSearch
                      onAfterChange={handleProfileSelect}
                    />
                  </Form.Item>
                  {!certificate?.certificateNo && (
                    <Form.Item label={AW_FORM_LABELS.manualOverride} className="aw-cert-field aw-manual-override-item">
                      <CrcCheckbox
                        label={AW_FORM_LABELS.ratingPlateDiffers}
                        checked={manualOverride}
                        disabled={readOnly}
                        onChange={(e) => setManualOverride(readCheckboxChecked(e))}
                        {...CRC_CHECKBOX_PROPS}
                      />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="testIssueDate"
                    label={AW_FORM_LABELS.testIssueDate}
                    rules={requiredRule(AW_FORM_LABELS.testIssueDate)}
                    className="aw-cert-field aw-cert-field--date"
                  >
                    <DatePicker className="aw-form-datepicker aw-cert-datepicker" {...datePickerProps} />
                  </Form.Item>
                  <Form.Item name="intervalMonths" label={AW_FORM_LABELS.intervalMonths} className="aw-cert-field">
                    <InputNumber min={1} max={60} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="expiryDate"
                    label={AW_FORM_LABELS.expiryDate}
                    tooltip="Auto-calculated from Test / Issue Date + Interval (Months). Not editable."
                    className="aw-cert-field aw-cert-field--date"
                  >
                    <DatePicker className="aw-form-datepicker aw-cert-datepicker" {...datePickerProps} disabled />
                  </Form.Item>
                  {certificate?.certificateNo && (
                    <Form.Item label={AW_FORM_LABELS.manualOverride} className="aw-cert-field aw-manual-override-item">
                      <CrcCheckbox
                        label={AW_FORM_LABELS.ratingPlateDiffers}
                        checked={manualOverride}
                        disabled={readOnly}
                        onChange={(e) => setManualOverride(readCheckboxChecked(e))}
                        {...CRC_CHECKBOX_PROPS}
                      />
                    </Form.Item>
                  )}
                </div>
                <Form.Item name="siteName" hidden>
                  <Input />
                </Form.Item>
              </div>
            </Col>
            <Col xs={24} lg={10} xl={9}>
              <div className="aw-section aw-section--profile-card">
                <div className="aw-section-title">{AW_CERTIFICATE_SECTIONS.EQUIPMENT_PROFILE}</div>
                {profileFields ? (
                  <dl className="aw-profile-spec-table">
                    {profileFields.map(([label, val]) => (
                      <div key={label} className="aw-profile-spec-row">
                        <dt>{label}</dt>
                        <dd>{val ?? '—'}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="aw-profile-empty">Select an equipment profile to view register details.</p>
                )}
              </div>
            </Col>
          </Row>

          <div className="aw-section aw-section--form">
            <div className="aw-section-title">{AW_CERTIFICATE_SECTIONS.VERIFICATION_CONFIG}</div>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item name="process" label={AW_FORM_LABELS.process}>
                  <AwFormSelect
                    options={formOptions.process}
                    placeholder="Select Process"
                    allowClear
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="outputMode" label={AW_FORM_LABELS.outputMode}>
                  <AwFormSelect
                    options={formOptions.outputMode}
                    placeholder="Select Output Mode"
                    allowClear
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="grade" label={AW_FORM_LABELS.grade}>
                  <AwFormSelect
                    options={formOptions.grade}
                    placeholder="Select Grade"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="verificationRange" label={AW_FORM_LABELS.verificationRange}>
                  <AwFormSelect
                    options={formOptions.verificationRange}
                    placeholder="Select Verification Range"
                    allowClear
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="rangeRecorded" label={AW_FORM_LABELS.rangeRecorded}>
                  <Input placeholder="Enter Range Recorded" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="supplyVoltageV" label={AW_FORM_LABELS.supplyVoltage}>
                  <Input placeholder="Enter Supply Voltage" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="supplyFrequency" label={AW_FORM_LABELS.supplyFrequency}>
                  <Input placeholder="Enter Supply Frequency" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="wfsUnit" label={AW_FORM_LABELS.wfsUnit}>
                  <AwFormSelect
                    options={formOptions.wfsUnit}
                    placeholder="Select WFS Unit"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="ambientTempC" label={AW_FORM_LABELS.ambientTemp} rules={requiredRule(AW_FORM_LABELS.ambientTemp)}>
                  <InputNumber style={{ width: '100%' }} min={-20} max={50} step={0.1} placeholder="15–35" />
                </Form.Item>
              </Col>
            </Row>
          </div>

        <div className="aw-test-points-grid">
        {AW_PARAMETERS.map((param) => (
          <ArcWeldingTestPointBlock
            key={param}
            parameter={param}
            defaultCollapsed
            valueClassification={parameterState[param]?.valueClassification}
            method={parameterState[param]?.method}
            permittedTolerance={formatPermittedToleranceDisplay(parameterState[param])}
            sectionResult={parameterState[param]?.sectionResult}
            points={parameterState[param]?.points}
            readOnly={readOnly}
            valueClassifications={formOptions.valueClassification}
            onClassificationChange={(v) =>
              setParameterState((prev) => ({
                ...prev,
                [param]: {
                  ...prev[param],
                  valueClassification: v,
                  method: methodForClassification(v),
                  sectionResult: v === 'Not tested' ? 'Incomplete' : prev[param]?.sectionResult,
                },
              }))
            }
            onPointChange={(points) =>
              setParameterState((prev) => ({
                ...prev,
                [param]: { ...prev[param], points },
              }))
            }
          />
        ))}
        </div>

        <div className="aw-section aw-ref-instruments-section">
          <div className="aw-section-title-row">
            <div className="aw-section-title">{AW_CERTIFICATE_SECTIONS.REFERENCE_INSTRUMENTS}</div>
            {!readOnly && (
              <CrcButton
                label="Add Instrument"
                variant="primary"
                size="small"
                icon={Plus}
                onClick={() => setRefInstruments((prev) => [...prev, emptyRefInstrument()])}
              />
            )}
          </div>
          <div className="aw-ref-instruments-table-wrap">
            <Table
              size="small"
              rowKey="key"
              pagination={false}
              columns={refColumns}
              dataSource={refInstruments}
              className="aw-ref-instruments-table"
            />
          </div>
        </div>

        <div className="aw-section">
          <div className="aw-section-title">{AW_CERTIFICATE_SECTIONS.PROCEDURAL}</div>
          <Row gutter={[16, 8]} className="aw-procedural-list">
            {proceduralFields.map((field) => (
              <Col xs={24} sm={12} key={field.key}>
                <CrcCheckbox
                  className="aw-procedural-checkbox"
                  label={field.label}
                  checked={Boolean(procedural[field.key])}
                  disabled={readOnly}
                  onChange={(e) =>
                    setProcedural((prev) => ({
                      ...prev,
                      [field.key]: readCheckboxChecked(e),
                    }))
                  }
                  {...CRC_CHECKBOX_PROPS}
                />
              </Col>
            ))}
          </Row>
        </div>

          <div className="aw-section">
            <div className="aw-section-title">{AW_CERTIFICATE_SECTIONS.REMARKS}</div>
            <Row>
              <Col xs={24} md={12}>
                <Form.Item name="remarks" label={AW_FORM_LABELS.remarks}>
                  <TextArea rows={4} placeholder="Enter Remarks" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="aw-section aw-authorisation-section">
            <div className="aw-section-title">{AW_CERTIFICATE_SECTIONS.AUTHORISATION}</div>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <AwSignatureField
                  label={AW_FORM_LABELS.technician}
                  value={technicianAuth}
                  disabled={readOnly}
                  onChange={setTechnicianAuth}
                />
              </Col>
              <Col xs={24} md={12}>
                <AwSignatureField
                  label={AW_FORM_LABELS.supervisor}
                  value={supervisorAuth}
                  disabled={readOnly || status === AW_CERTIFICATE_STATUS.DRAFT}
                  onChange={setSupervisorAuth}
                  helperText={
                    status === AW_CERTIFICATE_STATUS.DRAFT
                      ? 'Supervisor signs after Submit for Review. Only the Technician signs at this stage.'
                      : 'Save to store supervisor name. Use Authorise & Issue to sign and issue the certificate.'
                  }
                />
              </Col>
            </Row>
          </div>
        </Form>
      </div>

      <ArcWeldingShareModal
        open={shareModalOpen}
        certificateId={certificate?.id}
        certificateNo={certificate?.certificateNo}
        onClose={() => setShareModalOpen(false)}
      />
    </Spin>
  );
};

export default ArcWeldingCertificateForm;
